import { useState, useEffect, useCallback, Fragment, useRef, useMemo } from 'react';
import type { Player, BoardCell, PropertyOwnership } from '../../types';
import { CHARACTERS, IDENTITIES, REVERSE_CARDS } from '../../data';
import { getCharPortraitPath } from '../../data/assets';
import { useRoomStore } from '../../store/roomStore';
import styles from './PlayerList.module.css';

const PLAYER_COLORS = ['#FF4444', '#44CC88', '#4488FF', '#FFAA00'];

interface Props {
  players: Player[];
  currentPlayerIndex: number;
  board: BoardCell[];
  properties: PropertyOwnership[];
  selectedPlayerId: string | null;
  onSelectPlayer: (id: string | null) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  isMobile?: boolean;
  isPortraitMobile?: boolean;
}

function PlayerDetail({ player, board, properties }: { player: Player; board: BoardCell[]; properties: PropertyOwnership[] }) {
  const char = CHARACTERS.find(c => c.id === player.characterId);
  const identity = player.identityId ? IDENTITIES.find(i => i.id === player.identityId) : null;
  const color = PLAYER_COLORS[player.id === 'player_1' ? 0 : player.id === 'player_2' ? 1 : player.id === 'player_3' ? 2 : 3] || PLAYER_COLORS[0];

  const myProps = player.properties
    .map(pid => ({ cell: board[pid], prop: properties.find(p => p.cellId === pid) }))
    .filter(x => x.cell);

  const propertyValue = myProps.reduce((s, x) => s + (x.cell?.price || 0), 0);
  const upgradeValue = myProps.reduce((s, x) => s + (x.prop && x.prop.buildingLevel > 0 ? (x.cell?.upgradeCost || 0) * x.prop.buildingLevel : 0), 0);
  const totalAssets = player.money + propertyValue + upgradeValue;

  const cards = player.reverseCards.map(id => REVERSE_CARDS.find(c => c.id === id)).filter(Boolean);

  return (
    <div className={styles.detail}>
      {/* 头部 */}
      <div className={styles.detailHeader} style={{ borderColor: color }}>
        <img src={getCharPortraitPath(player.characterId)} alt="" className={styles.detailAvatar} />
        <div className={styles.detailHeaderInfo}>
          <div className={styles.detailName}>{player.name}</div>
          <div className={styles.detailChar}>{char?.emoji} {char?.name}</div>
        </div>
        <div className={styles.detailMoney}>¥{player.money.toLocaleString()}</div>
      </div>

      {/* 身份 */}
      {identity && (
        <div className={styles.detailRow}>
          <span>{player.identityRevealed ? identity.emoji : '❓'}</span>
          <span style={{ color: player.identityRevealed ? '#6c5ce7' : 'rgba(92, 64, 51, 0.3)', fontStyle: player.identityRevealed ? 'normal' : 'italic' }}>
            {player.identityRevealed ? identity.name : '身份隐藏'}
          </span>
        </div>
      )}

      {/* 资产摘要 */}
      <div className={styles.detailSummary}>
        <div className={styles.detailSummaryItem}>
          <span>总资产</span>
          <span>¥{totalAssets.toLocaleString()}</span>
        </div>
        <div className={styles.detailSummaryItem}>
          <span>地产</span>
          <span>{myProps.length} 处</span>
        </div>
        <div className={styles.detailSummaryItem}>
          <span>反转卡</span>
          <span>{player.reverseCards.length}/3</span>
        </div>
      </div>

      {/* 地产列表 */}
      {myProps.length > 0 && (
        <div className={styles.detailSection}>
          <div className={styles.detailSectionTitle}>🏠 地产</div>
          {myProps.map(({ cell, prop }) => cell && (
            <div key={cell.id} className={styles.detailPropItem}>
              <span>{cell.emoji}</span>
              <span className={styles.detailPropName}>{cell.name}</span>
              {prop && prop.buildingLevel > 0 && (
                <span className={styles.detailPropLevel}>
                  {prop.buildingLevel === 1 ? '🏠' : prop.buildingLevel === 2 ? '🏡' : '🏨'} Lv.{prop.buildingLevel}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 状态标记 */}
      {(player.inJail || player.immuneTurns > 0 || player.skipNextTurn || player.allianceWith) && (
        <div className={styles.detailStatusRow}>
          {player.inJail && <span className={styles.detailStatusBadge}>🔒 监狱中 ({player.jailTurns}回合)</span>}
          {player.immuneTurns > 0 && <span className={styles.detailStatusBadge}>🛡️ 免疫×{player.immuneTurns}</span>}
          {player.allianceWith && <span className={styles.detailStatusBadge}>🤝 联盟中 ({player.allianceTurns}回合)</span>}
          {player.skipNextTurn && <span className={styles.detailStatusBadge}>⏭️ 跳过回合</span>}
        </div>
      )}
    </div>
  );
}

export default function PlayerList({ players, currentPlayerIndex, board, properties, selectedPlayerId, onSelectPlayer, collapsed, onToggleCollapse, isMobile, isPortraitMobile }: Props) {
  const currentPlayer = players[currentPlayerIndex];
  const myPlayerName = useRoomStore(s => s.myPlayerName);

  // 显示顺序：本地玩家排首位（不影响 players[] 行棋顺序）
  const displayPlayers = useMemo(() => {
    if (!myPlayerName) return players;
    const myName = myPlayerName.trim().toLowerCase();
    const selfIdx = players.findIndex(p => p.name.trim().toLowerCase() === myName);
    if (selfIdx < 0) return players;  // 未找到自己
    if (selfIdx === 0) return players; // 已经是首位
    return [players[selfIdx], ...players.filter((_, i) => i !== selfIdx)];
  }, [players, myPlayerName]);

  // 判断某个玩家是否为"当前回合玩家"（基于原始 players[] 索引）
  const isCurrentPlayer = useCallback((p: typeof players[number]) => {
    const idx = players.indexOf(p);
    return idx === currentPlayerIndex;
  }, [players, currentPlayerIndex]);

  // ====== 竖屏移动端拖拽 ======
  const [playerListDragOffset, setPlayerListDragOffset] = useState({ x: 0, y: 0 });
  const [isPlayerListDragging, setIsPlayerListDragging] = useState(false);
  const playerListDragRef = useRef({
    startX: 0, startY: 0,
    startOffsetX: 0, startOffsetY: 0,
    moved: false,
  });

  const handlePLPointerDown = useCallback((e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.closest('button')) return;
    e.preventDefault();
    playerListDragRef.current = {
      startX: e.clientX, startY: e.clientY,
      startOffsetX: playerListDragOffset.x, startOffsetY: playerListDragOffset.y,
      moved: false,
    };
    setIsPlayerListDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [playerListDragOffset.x, playerListDragOffset.y]);

  const handlePLPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isPlayerListDragging) return;
    const dx = e.clientX - playerListDragRef.current.startX;
    const dy = e.clientY - playerListDragRef.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      playerListDragRef.current.moved = true;
    }
    if (playerListDragRef.current.moved) {
      e.preventDefault(); e.stopPropagation();
    }
    setPlayerListDragOffset({
      x: playerListDragRef.current.startOffsetX + dx,
      y: playerListDragRef.current.startOffsetY + dy,
    });
  }, [isPlayerListDragging]);

  const handlePLPointerUp = useCallback(() => {
    setIsPlayerListDragging(false);
  }, []);

  useEffect(() => {
    const onGlobalUp = () => setIsPlayerListDragging(false);
    window.addEventListener('pointerup', onGlobalUp);
    return () => window.removeEventListener('pointerup', onGlobalUp);
  }, []);

  // 横竖屏切换 + 折叠状态变化时重置拖拽位置
  useEffect(() => {
    setPlayerListDragOffset({ x: 0, y: 0 });
    playerListDragRef.current.moved = false;
  }, [isPortraitMobile, collapsed]);

  const playerListDragStyle: React.CSSProperties = isPortraitMobile ? {
    transform: `translate(${playerListDragOffset.x}px, ${playerListDragOffset.y}px)`,
    transition: isPlayerListDragging ? 'none' : 'transform 0.15s ease-out',
    cursor: isPlayerListDragging ? 'grabbing' : 'grab',
  } : {
    // PC端也支持拖拽
    transform: `translate(${playerListDragOffset.x}px, ${playerListDragOffset.y}px)`,
    transition: isPlayerListDragging ? 'none' : 'transform 0.15s ease-out',
    cursor: isPlayerListDragging ? 'grabbing' : 'grab',
  };

  // ====== 竖屏移动端：右侧悬浮面板（类似PC侧边栏，更窄，固定定位） ======
  if (isPortraitMobile) {
    return (
      <div
        className={`${styles.container} ${styles.portraitSidebar} ${collapsed ? styles.collapsed : ''} ${isPlayerListDragging ? styles.dragging : ''}`}
        style={playerListDragStyle}
        onPointerDown={handlePLPointerDown}
        onPointerMove={handlePLPointerMove}
        onPointerUp={handlePLPointerUp}
      >
        {/* 折叠/展开按钮 */}
        <button className={styles.toggleBtn} onClick={onToggleCollapse} title={collapsed ? '展开玩家列表' : '收起玩家列表'}>
          {collapsed ? '◀' : '▶'}
        </button>

        {!collapsed && (
          <div className={`${styles.inner} ${styles.portraitInner}`}>
            <div className={styles.title}>玩家</div>
            {/* 玩家列表 */}
            <div className={styles.list}>
              {displayPlayers.map((p) => {
                const isCurrent = isCurrentPlayer(p);
                const isSelected = p.id === selectedPlayerId;
                const originalIdx = players.indexOf(p);
                const color = PLAYER_COLORS[originalIdx % PLAYER_COLORS.length];
                return (
                  <Fragment key={p.id}>
                    <button
                      className={`${styles.playerItem} ${isCurrent ? styles.playerItemCurrent : ''} ${isSelected ? styles.playerItemSelected : ''} ${p.bankrupt ? styles.playerItemBankrupt : ''} ${p.disconnected ? styles.playerItemDisconnected : ''}`}
                      style={{ borderLeftColor: isCurrent || isSelected ? color : 'transparent' }}
                      onClick={() => onSelectPlayer(isSelected ? null : p.id)}
                    >
                      <div className={styles.avatarWrap}>
                        <img src={getCharPortraitPath(p.characterId)} alt="" className={styles.playerItemAvatar} />
                        {p.disconnected && <span className={styles.disconnectedDot} title="掉线中" />}
                      </div>
                      <div className={styles.playerItemInfo}>
                        <span className={styles.playerItemName}>
                          {p.name}
                          {isCurrent && <span className={styles.currentDot} style={{ background: color }}>●</span>}
                          {p.disconnected && <span className={styles.disconnectedBadge}>🔌 AI托管</span>}
                        </span>
                        <span className={styles.playerItemMoney}>¥{p.money.toLocaleString()}</span>
                      </div>
                      {p.bankrupt && <span className={styles.bankruptLabel}>破产</span>}
                      {!p.bankrupt && (
                        <span className={styles.expandHint}>{isSelected ? '▲' : '▼'}</span>
                      )}
                    </button>
                    {isSelected && !p.bankrupt && (
                      <PlayerDetail
                        player={p}
                        board={board}
                        properties={properties}
                      />
                    )}
                  </Fragment>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ====== 横屏移动端：底部浮层面板 ======
  if (isMobile) {
    return (
      <div className={`${styles.mobileOverlay} ${!collapsed ? styles.mobileOverlayVisible : ''}`} onClick={() => onToggleCollapse()}>
        <div className={styles.mobilePanel} onClick={e => e.stopPropagation()}>
          <div className={styles.mobileHeader}>
            <span className={styles.mobileHeaderTitle}>👥 玩家列表</span>
            <button className={styles.mobileCloseBtn} onClick={() => onToggleCollapse()}>✕</button>
          </div>
          <div className={styles.list}>
            {displayPlayers.map((p) => {
              const isCurrent = isCurrentPlayer(p);
              const isSelected = p.id === selectedPlayerId;
              const originalIdx = players.indexOf(p);
              const color = PLAYER_COLORS[originalIdx % PLAYER_COLORS.length];
              return (
                <button
                  key={p.id}
                  className={`${styles.playerItem} ${isCurrent ? styles.playerItemCurrent : ''} ${isSelected ? styles.playerItemSelected : ''} ${p.bankrupt ? styles.playerItemBankrupt : ''} ${p.disconnected ? styles.playerItemDisconnected : ''}`}
                  style={{ borderLeftColor: isCurrent || isSelected ? color : 'transparent' }}
                  onClick={() => onSelectPlayer(isSelected ? null : p.id)}
                >
                  <div className={styles.avatarWrap}>
                    <img src={getCharPortraitPath(p.characterId)} alt="" className={styles.playerItemAvatar} />
                    {p.disconnected && <span className={styles.disconnectedDot} title="掉线中" />}
                  </div>
                  <div className={styles.playerItemInfo}>
                    <span className={styles.playerItemName}>
                      {p.name}
                      {isCurrent && <span className={styles.currentDot} style={{ background: color }}>●</span>}
                      {p.disconnected && <span className={styles.disconnectedBadge}>🔌 AI托管</span>}
                    </span>
                    <span className={styles.playerItemMoney}>¥{p.money.toLocaleString()}</span>
                  </div>
                  {p.bankrupt && <span className={styles.bankruptLabel}>破产</span>}
                </button>
              );
            })}
          </div>
          {selectedPlayerId && (
            <PlayerDetail
              player={players.find(p => p.id === selectedPlayerId)!}
              board={board}
              properties={properties}
            />
          )}
        </div>
      </div>
    );
  }

  // ====== PC端 / 平板：右侧边栏（可拖拽，可折叠） ======
  return (
    <div className={`${styles.container} ${collapsed ? styles.collapsed : ''} ${isPlayerListDragging ? styles.dragging : ''}`}
      style={playerListDragStyle}>
      {/* 折叠/展开按钮 — 收起时作为浮动按钮（可拖拽） */}
      {collapsed ? (
        <button
          className={styles.toggleBtn}
          onClick={onToggleCollapse}
          title="展开玩家列表"
          onPointerDown={handlePLPointerDown}
          onPointerMove={handlePLPointerMove}
          onPointerUp={handlePLPointerUp}
          style={{ touchAction: 'none' }}
        >
          ◀
        </button>
      ) : (
        <>
          <button className={styles.toggleBtn} onClick={onToggleCollapse} title="收起玩家列表">
            ▶
          </button>

          <div
            className={styles.inner}
            onPointerDown={handlePLPointerDown}
            onPointerMove={handlePLPointerMove}
            onPointerUp={handlePLPointerUp}
          >
            <div className={styles.title}>玩家</div>
            {/* 玩家列表 */}
            <div className={styles.list}>
              {displayPlayers.map((p) => {
                const isCurrent = isCurrentPlayer(p);
                const isSelected = p.id === selectedPlayerId;
                const originalIdx = players.indexOf(p);
                const color = PLAYER_COLORS[originalIdx % PLAYER_COLORS.length];
                return (
                  <Fragment key={p.id}>
                    <button
                      className={`${styles.playerItem} ${isCurrent ? styles.playerItemCurrent : ''} ${isSelected ? styles.playerItemSelected : ''} ${p.bankrupt ? styles.playerItemBankrupt : ''} ${p.disconnected ? styles.playerItemDisconnected : ''}`}
                      style={{ borderLeftColor: isCurrent || isSelected ? color : 'transparent' }}
                      onClick={() => onSelectPlayer(isSelected ? null : p.id)}
                    >
                      <div className={styles.avatarWrap}>
                        <img src={getCharPortraitPath(p.characterId)} alt="" className={styles.playerItemAvatar} />
                        {p.disconnected && <span className={styles.disconnectedDot} title="掉线中" />}
                      </div>
                      <div className={styles.playerItemInfo}>
                        <span className={styles.playerItemName}>
                          {p.name}
                          {isCurrent && <span className={styles.currentDot} style={{ background: color }}>●</span>}
                          {p.disconnected && <span className={styles.disconnectedBadge}>🔌 AI托管</span>}
                        </span>
                        <span className={styles.playerItemMoney}>¥{p.money.toLocaleString()}</span>
                      </div>
                      {p.bankrupt && <span className={styles.bankruptLabel}>破产</span>}
                      {!p.bankrupt && (
                        <span className={styles.expandHint}>{isSelected ? '▲' : '▼'}</span>
                      )}
                    </button>
                    {isSelected && !p.bankrupt && (
                      <PlayerDetail
                        player={p}
                        board={board}
                        properties={properties}
                      />
                    )}
                  </Fragment>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
